import { requirementsApi } from '../services/api';

/**
 * #579 (D-07, second half): graceful 409 handling for the NON-EDITOR update flows.
 *
 * #572 made `PUT /items/{id}` reject a missing `version` with 428 and taught six
 * frontend flows to send one. That closed the silent-clobber hole, but it left the
 * five non-editor flows surfacing a real concurrent-edit **409** as a generic error
 * string — and the tree rename as a bare `console.error`, i.e. nothing at all. The
 * behaviour was correct; the user just had no way forward.
 *
 * The main editor already has the field-level merge dialog (#161), which is wired
 * into `ItemDetails`' autosave and needs both versions of every field. That is far
 * too heavy for a one-field quick action (a rename, an attribute stamp, applying an
 * AI suggestion), so these flows get the lighter affordance the issue asks for:
 * **Reload** (discard mine, take theirs) or **Keep mine** (re-fetch the version and
 * re-apply the same edit on top).
 *
 * The prompt is a single app-level host rather than a dialog per call site, because
 * three of the five flows live in hooks (`useAiFunctions`, `useTreeState`) or an
 * inline callback (`RequirementsTreeView`) that render no JSX of their own.
 */

/** The registered prompt host, set by `<ConflictPromptHost />` on mount. */
let promptHost = null;

/** Internal: `<ConflictPromptHost />` registers itself here. Returns an unregister. */
export function registerConflictHost(fn) {
  promptHost = fn;
  return () => { if (promptHost === fn) promptHost = null; };
}

/** True for a genuine optimistic-concurrency conflict from `PUT /items/{id}`. */
export function isVersionConflict(err) {
  return err?.response?.status === 409;
}

/**
 * The server's copy of the item as carried on the 409 (`detail.current`), or null.
 * `api.py` puts it there precisely so the client can show/keep the latest state
 * without a second round trip.
 */
export function conflictCurrent(err) {
  const detail = err?.response?.data?.detail;
  return (detail && typeof detail === 'object' && detail.current) || null;
}

/**
 * Ask the user how to resolve a conflict. Resolves to `'keep'` or `'reload'`.
 *
 * With no host mounted (a unit test, or a surface that renders outside the app
 * shell) this returns `'reload'`: the fail-safe answer is to DISCARD the local edit,
 * never to silently overwrite someone else's change — which is the entire point of
 * #572/#579.
 */
export function requestConflictResolution(info) {
  if (!promptHost) return 'reload';
  return promptHost(info);
}

/**
 * `requirementsApi.updateItem` plus the 409 affordance.
 *
 * Returns `{ saved }` when the write applied (first try, or after the user chose
 * "Keep mine"), or `{ conflict: true, reloaded: true }` when the user chose to take
 * the server's copy — in which case NOTHING was written. Every caller refreshes its
 * own view afterwards either way, so the two outcomes converge on the same UI action.
 *
 * Non-409 errors are re-thrown untouched: this changes the conflict path only.
 */
export async function saveWithConflictPrompt(itemId, patch, options = {}) {
  const { label } = options;
  try {
    return { saved: await requirementsApi.updateItem(itemId, patch) };
  } catch (err) {
    if (!isVersionConflict(err)) throw err;
    const choice = await requestConflictResolution({
      itemId,
      label: label || itemId,
      current: conflictCurrent(err),
    });
    if (choice !== 'keep') return { conflict: true, reloaded: true };
    // "Keep mine": re-read ONLY to pick up the version, then re-apply the same edit
    // on top of their copy. Deliberately a fresh GET rather than the `current` on the
    // 409 payload — a third save could have landed while the dialog was open, and
    // re-using a version we know is old would just 409 again.
    const current = await requirementsApi.getItem(itemId);
    return {
      saved: await requirementsApi.updateItem(itemId, { ...patch, version: current.version }),
      overwrote: true,
    };
  }
}
